import { Layout } from "../../components/Layout";
import { useEffect, useRef } from "react";
import { BUTTON_COLORS, BUTTON_TYPE } from "../../components/Button";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader } from "../../components/Loader";
import { usePermissions } from "../../providers/authProvider";
import { useCheckoutData } from "../../hooks/useCheckoutData";
import { log } from "../../utils/logger";
import { CART_URL, USER_AUTH_URL } from "../../config";
import { CheckoutSteps } from "../../components/checkout/CheckoutSteps";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string
);
const logger = log.scope("CheckoutSuccessPage");

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const params = useParams();
  const userPermissions = usePermissions();

  const [checkoutState, , getCheckout] = useCheckoutData();

  const init = useRef(true);
  useEffect(() => {
    if (
      userPermissions?.loading ||
      !userPermissions ||
      init.current === false
    ) {
      return;
    }
    if (!userPermissions.data?.loggedIn || !userPermissions.data.user?.email) {
      init.current = false;
      navigate(USER_AUTH_URL("login", { flow: "checkout" }));
      return;
    }
    const user = userPermissions?.data?.user;
    if (!params.checkoutId) {
      navigate(CART_URL());
    }

    if (user && params.checkoutId) {
      init.current = false;
      getCheckout({
        id: params.checkoutId,
      });
    }
  }, [
    userPermissions?.loading,
    userPermissions?.data?.user,
    params.checkoutId,
  ]);

  logger.debug("Checkout render", {
    params: { ...params },
    userId: userPermissions?.data?.user?.id,
    checkoutStatus: checkoutState.status,
  });

  //TODO: handle not finished checkout state (redirect to checkout?)

  // Render different views based on checkout state
  const renderContent = () => {
    // Loading state
    if (checkoutState.status === "loading" || checkoutState.status === "idle") {
      return (
        <div className="p-5">
          <Loader>Loading checkout...</Loader>
        </div>
      );
    }

    // Error state (validation/cart errors)
    if (checkoutState.status === "error") {
      const { error, errorInfo } = checkoutState;

      // Generic error
      return (
        <div className="p-5">
          <div>
            <h1>{error}</h1>
            <h2>{JSON.stringify(errorInfo as any)}</h2>
          </div>
          <div className="flex justify-center">
            <Link
              to={CART_URL()}
              className={BUTTON_TYPE.large + BUTTON_COLORS.primary}
            >
              Back to Cart
            </Link>
          </div>
        </div>
      );
    }

    // Redirect success state (payment completed)
    if (checkoutState.status === "redirect-success") {
      const { orderSlug, contactEmail, deliveryLocation, status, payment } =
        checkoutState.data.checkout;
      return (
        <div className="flex-1 max-w-full">
          {orderSlug ? (
            <div>
              <div className=" p-auto flex flex-col mx-2">
                {Elements ? (
                  <Elements
                    options={{
                      clientSecret: payment?.clientSecret,
                      appearance: {
                        theme: "stripe" as const,
                      },
                    }}
                    stripe={stripePromise}
                  >
                    <CheckoutSteps
                      // cartSlug={cartSlug}
                      orderSlug={orderSlug}
                      onSubmit={() => {}}
                      stepIdx={2}
                      showAll
                      steps={["email", "location", "status"]}
                      contactEmail={contactEmail}
                      deliveryLocation={deliveryLocation}
                      status={{ status }}
                    />
                  </Elements>
                ) : null}
                ;
              </div>
              <div className="p-10">
                <Link
                  className={BUTTON_TYPE.large + BUTTON_COLORS.primary}
                  id="retry-button"
                  to="/"
                >
                  Shop for more!
                </Link>
              </div>
            </div>
          ) : (
            <div>
              Something went wrong with the order. Please reach out to{" "}
              <a href="mailto:sales@doridium.com">sales@doridium.com</a> to see
              if your order went through
            </div>
          )}
        </div>
      );
    }

    // Fallback (should never reach here)
    return (
      <div className="p-5">
        An unexpected error occurred. Please refresh the page.
      </div>
    );
  };

  return (
    <Layout loading={checkoutState.status === "loading"} headerStyle="sm">
      <div>{renderContent()}</div>
    </Layout>
  );
}
